import { useMemo } from "react";
import { sortBy } from "lodash";
import { addHours, format, parseISO, startOfHour } from "date-fns";
import { getNfbTherapistsForRange, useNfbTherapistsOnSchedule } from "../useNfbTherapistsOnSchedule";
import { NfbTherapistSchedule, PriorityBlock, PrioritySession, TechPoolEntry, TimeOffInterval } from "../types.nfbAssign";

const getInSessionByUserId = (sessions: Array<PrioritySession>) => {
	const sessionsByUserId = new Map<string, PrioritySession>();
	sessions
		.filter((session) => session.status === "in_session")
		.forEach((session) => {
			const userId = session.technician?.user_id?.id;
			if (userId) sessionsByUserId.set(userId, session);
		});
	return sessionsByUserId;
};

// assigned to any awaiting_in_clinic session of the day
const getAwaitingByUserId = (sessions: Array<PrioritySession>) => {
	const sessionsByUserId = new Map<string, PrioritySession>();
	sortBy(
		sessions.filter((session) => session.status === "awaiting_in_clinic"),
		"start_date"
	).forEach((session) => {
		const userId = session.technician?.user_id?.id;
		if (userId && !sessionsByUserId.has(userId)) sessionsByUserId.set(userId, session);
	});
	return sessionsByUserId;
};

// confirmed session in the current block also flags "atribuído", but keeps the free ring color
const getConfirmedByUserId = (currentBlock: PriorityBlock | undefined) => {
	const sessionsByUserId = new Map<string, PrioritySession>();
	(currentBlock?.sessions ?? [])
		.filter((session) => session.status === "confirmed")
		.forEach((session) => {
			const userId = session.technician?.user_id?.id;
			if (userId && !sessionsByUserId.has(userId)) sessionsByUserId.set(userId, session);
		});
	return sessionsByUserId;
};

// any therapist with a schedule touching the current clock hour (17:30 → 17:00-18:00);
// before a shift the hour is empty, so pre-show the techs of the day's next shift start
const getScheduledTherapists = (
	therapistSchedules: Array<NfbTherapistSchedule>,
	timeOffIntervals: Array<TimeOffInterval>,
	now: Date
) => {
	const hourStart = startOfHour(now);
	const onThisHour = getNfbTherapistsForRange(therapistSchedules, timeOffIntervals, hourStart, addHours(hourStart, 1));
	if (onThisHour.length > 0) return onThisHour;

	const upcomingStartTimes = therapistSchedules
		.map((schedule) => schedule.start.getTime())
		.filter((startTime) => startTime > now.getTime());
	if (upcomingStartTimes.length === 0) return onThisHour;

	const nextShiftStart = new Date(Math.min(...upcomingStartTimes));
	const onNextShift = getNfbTherapistsForRange(
		therapistSchedules,
		timeOffIntervals,
		nextShiftStart,
		new Date(nextShiftStart.getTime() + 1)
	);
	return onNextShift;
};

const isHappeningNow = (session: PrioritySession, now: Date) => {
	const startMs = parseISO(session.start_date).getTime();
	const endMs = parseISO(session.end_date).getTime();
	const happeningNow = startMs <= now.getTime() && endMs > now.getTime();
	return happeningNow;
};

// participants of a meeting happening now count as busy, whatever the meeting's status (the fetch
// already limits meetings to the blocking statuses); maps each to their meeting so a scheduled
// therapist's chip can open it in the preview
const getMeetingBusyByUserId = (meetings: Array<PrioritySession>, now: Date) => {
	const meetingByUserId = new Map<string, PrioritySession>();

	meetings
		.filter((meeting) => isHappeningNow(meeting, now))
		.forEach((meeting) => {
			(meeting.internal_meetings_participants ?? []).forEach((participant) => {
				const userId = participant?.technicians_id?.user_id?.id;
				if (!userId || meetingByUserId.has(userId)) return;
				meetingByUserId.set(userId, meeting);
			});
		});

	return meetingByUserId;
};

export const useTechPool = (
	sessions: Array<PrioritySession>,
	meetings: Array<PrioritySession>,
	currentBlock: PriorityBlock | undefined,
	clinics: Array<number>,
	now: Date
) => {
	const { therapistSchedules, timeOffIntervals } = useNfbTherapistsOnSchedule(clinics, now);

	const techPool = useMemo(() => {
		const inSessionByUserId = getInSessionByUserId(sessions);
		const awaitingByUserId = getAwaitingByUserId(sessions);
		const confirmedByUserId = getConfirmedByUserId(currentBlock);
		const meetingBusyByUserId = getMeetingBusyByUserId(meetings, now);
		const scheduledTherapists = getScheduledTherapists(therapistSchedules, timeOffIntervals, now);

		const inSessionEntries: Array<TechPoolEntry> = sortBy(
			Array.from(inSessionByUserId.entries()),
			([, session]) => session.start_date
		).map(([userId, session]) => ({
			therapist: {
				userId,
				firstName: session.technician?.user_id?.first_name,
				lastName: session.technician?.user_id?.last_name,
				avatar: session.technician?.user_id?.avatar
			},
			ringStatus: "in_session",
			subLabel: `até ${format(parseISO(session.end_date), "HH:mm")}`,
			session
		}));

		// only scheduled NFB therapists are shown; a meeting participant off the schedule is ignored
		const meetingEntries: Array<TechPoolEntry> = scheduledTherapists
			.filter((therapist) => !inSessionByUserId.has(therapist.userId) && meetingBusyByUserId.has(therapist.userId))
			.map((therapist) => ({
				therapist,
				ringStatus: "in_session",
				subLabel: "reunião",
				session: meetingBusyByUserId.get(therapist.userId)
			}));
		const inSession = [...inSessionEntries, ...meetingEntries];

		const free: Array<TechPoolEntry> = scheduledTherapists
			.filter((therapist) => !inSessionByUserId.has(therapist.userId) && !meetingBusyByUserId.has(therapist.userId))
			.map((therapist) => {
				const awaitingSession = awaitingByUserId.get(therapist.userId);
				const confirmedSession = confirmedByUserId.get(therapist.userId);
				const assignedSession = awaitingSession ?? confirmedSession;
				const ringStatus = awaitingSession ? "awaiting_in_clinic" : confirmedSession ? "confirmed" : "booked";
				return {
					therapist,
					ringStatus,
					subLabel: assignedSession ? "atribuído" : undefined,
					session: assignedSession
				};
			});

		return { inSession, free };
	}, [sessions, meetings, currentBlock, therapistSchedules, timeOffIntervals, now]);

	return techPool;
};
